import React, { useState, useEffect, useCallback, useMemo, FC, MouseEvent } from 'react';
import { Upload, Spin } from 'antd';

import { FileUploadTwoTone, FileUploadActiveTwoTone } from 'ui/components/Icon';
import { UploadFile, UploadInfo } from 'ui/components/Upload/types';
import { onCustomUpLoad } from 'ui/components/Upload/helpers';
import UploadItem from 'ui/components/Upload/UploadItem';

import { DraggerProps } from './types';

import { Wrapper, UploadIcon, UploadText, UploadHint } from './styled';

const UploadDragger = Upload.Dragger as FC<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Omit<DraggerProps<any>, 'onChange'> & {
    onMouseEnter?: (e: MouseEvent<HTMLDivElement>) => void;
    onMouseLeave?: (e: MouseEvent<HTMLDivElement>) => void;
    onChange?: (info: UploadInfo) => void;
  }
>;

const Dragger = <FileType extends Record<string, unknown>>({
  text,
  hint,
  style,
  accept,
  multiple,
  listType,
  className,
  loading,
  disabled,
  defaultFileList,
  maxSizeHint,
  setLoading,
  withPreviewList,
  withIcon,
  onDone,
  onChange,
  onRemove,
  onError,
  withoutConvertPath,
  customRequest,
  error,
  ...props
}: DraggerProps<FileType>) => {
  const [fileList, setFileList] = useState<UploadFile<FileType>[]>([]);
  const [isHover, setIsHover] = useState(false);

  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const handleChange = useCallback(
    (info: UploadInfo) => {
      const { status } = info.file;

      if (status === 'removed') {
        setFileList(info.fileList);
        if (onChange) {
          const filteredFileList = fileList.filter(file => file.uid !== info.file.uid);
          setFileList(filteredFileList);
        }
        if (onRemove) {
          onRemove(info.file);
        }
      }

      if (customRequest && status === 'uploading') {
        setFileList(info.fileList);
      }

      if (onDone && status === 'done') {
        onDone(info);
      } else if (onError && status === 'error') {
        onError(info);
      }
    },
    [customRequest, onDone, onError, onChange, onRemove, fileList],
  );

  useEffect(() => {
    if (defaultFileList) {
      setFileList(defaultFileList);
    }
  }, [defaultFileList]);

  useEffect(() => {
    if (onChange) onChange(fileList);
  }, [fileList, onChange]);

  const customRequestUpload = useCallback(
    async ({ file, onError, onProgress, onSuccess }) => {
      setLoading?.(true);
      const result = await onCustomUpLoad({
        file,
        onError,
        onProgress,
        onSuccess,
        withoutConvertPath,
      });
      setFileList((prev: UploadFile<FileType>[]) => [
        ...prev,
        { fileName: file.name, ...result, uid: file.uid },
      ]);
      setLoading?.(false);
    },
    [setLoading, withoutConvertPath],
  );

  const acceptLabel = useMemo(
    () => (accept ? `в формате ${accept.replace(/\./g, '')}` : ''),
    [accept],
  );

  return (
    <Wrapper style={style} className={className} disabled={disabled} error={error}>
      <Spin spinning={loading}>
        <UploadDragger
          accept={accept}
          multiple={multiple}
          fileList={fileList}
          disabled={disabled}
          customRequest={customRequest ?? customRequestUpload}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onChange={handleChange}
          {...props}
        >
          {withIcon && (
            <UploadIcon>{isHover ? <FileUploadActiveTwoTone /> : <FileUploadTwoTone />}</UploadIcon>
          )}

          {text && <UploadText disabled={disabled}>{text}</UploadText>}

          <UploadHint>
            {hint || (
              <>
                {acceptLabel}
                <br />
                Максимальный размер — {maxSizeHint}
              </>
            )}
          </UploadHint>
        </UploadDragger>

        {withPreviewList && (
          <Upload
            accept={accept}
            listType={listType}
            fileList={fileList}
            disabled={disabled}
            itemRender={(originNode, file, list, actions) => (
              <UploadItem
                file={file}
                fileList={list}
                originNode={originNode}
                listType={listType}
                actions={actions}
              />
            )}
            customRequest={customRequest ?? customRequestUpload}
            onChange={handleChange}
            {...props}
          />
        )}
      </Spin>
    </Wrapper>
  );
};

Dragger.defaultProps = {
  text: (
    <>
      <b>Загрузите</b> или перетащите файл сюда
    </>
  ),
  listType: 'picture',
  accept: '.png, .jpg',
  maxSizeHint: '100 Mb',
  multiple: true,
  loading: false,
  withPreviewList: true,
  withIcon: true,
};

export default Dragger;
